import React, { FC, useEffect, useState, Fragment } from 'react';
import { Typography } from '@mui/material';
import { LocalDentist, User } from 'models/user';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import UpdateLocalDentistForm from 'components/client-directory/client-settings/dentists/update-local-dentist-dialog/update-local-dentist-form.component';
import { LinkedOrganization } from 'models/organization';
import { getLabDefaultClinicId } from 'utils/lab-dentist-default-clinic';

type DentistsDetailsProps = {
    dentist: User;
    onUserUpdated: () => void;
};

const DentistsDetails: FC<DentistsDetailsProps> = ({ dentist, onUserUpdated }: DentistsDetailsProps) => {
    const { t: translate } = useTranslation(['common', 'users']);
    const currentOrganization = currentOrgVar();
    const [searchParams] = useSearchParams();
    const clinicIdFromUrl = searchParams.get('clinicId');
    const isWhiteLabel = searchParams.get('whiteLabel') === 'true';
    const [linkedOrganizations, setLinkedOrganizations] = useState<LinkedOrganization[]>([]);
    const [dentistToUpdate, setDentistToUpdate] = useState<LocalDentist>({
        _id: '',
        clinic: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        note: '',
        localId: '',
        active: false,
        local: undefined,
        labDefaultClinic: undefined,
    });

    useEffect(() => {
        if (!dentist) return;

        const currentOrgId = currentOrganization._id.toString();
        const uniqueLinkedOrganizations: LinkedOrganization[] = [];

        for (const { organization } of dentist.linkedOrganizations) {
            if (
                typeof organization === 'object' &&
                organization?.connectedLabs &&
                organization?.connectedLabs?.some(({ lab }) => lab._id.toString() === currentOrgId) &&
                organization?.active
            ) {
                uniqueLinkedOrganizations.push({
                    _id: organization._id?.toString() ?? '',
                    name: organization.name ?? '',
                });
            }
        }

        setLinkedOrganizations(uniqueLinkedOrganizations);
        // clinicId is needed to find the correct dentist note linked to the current lab, as dentist can have different notes for different clinics linked to the same lab.
        // When opened via a deep link without ?clinicId, fall back to the first linked clinic for the current lab.
        const clinicId = clinicIdFromUrl ?? uniqueLinkedOrganizations[0]?._id ?? '';

        const dentistNote = dentist.dentistNotes?.find(
            (note) => note.lab === currentOrganization._id && note.clinic === clinicId,
        );
        const dentistLocalId = dentist.dentistLocalIds?.find(
            (entry) => entry.lab === currentOrganization._id && entry.clinic === clinicId,
        );

        setDentistToUpdate({
            _id: dentist._id,
            clinic: clinicId,
            labDefaultClinic: getLabDefaultClinicId(dentist, clinicId),
            firstName: dentist.name.firstName,
            lastName: dentist.name.lastName,
            phone: dentist.phone || '',
            email: dentist.email || '',
            note: dentistNote?.note || '',
            localId: dentistLocalId?.localId || '',
            active: dentist.active ?? false,
            local: dentist.local,
        });
    }, [dentist]);

    return (
        <Fragment>
            <Typography mb={2} variant="h2">
                {translate('client-directory:DENTIST_DETAILS')}
            </Typography>
            <UpdateLocalDentistForm
                dentistToUpdate={dentistToUpdate}
                onUserUpdated={onUserUpdated}
                linkedOrganizations={linkedOrganizations}
                isWhiteLabel={isWhiteLabel}
            />
        </Fragment>
    );
};

export default DentistsDetails;
